import { ErrorBoundary } from '@sentry/react';
import {
  AxiosRequestConfig,
  AxiosResponse,
  RequestConfig,
  RequestError,
  getLocale,
  history,
} from '@umijs/max';
import { message, notification } from 'antd';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { cloneDeep } from 'lodash';
import Fallback from './components/Fallback';
import { ENABLE_HANDLE_401 } from './constants';
import { ERROR_TYPES } from './constants/eventTracking';
import { trackError } from './utils/gtag';
import {
  COOKIE_FIRST_AUTH_TOKEN,
  LOCAL_TOKEN,
  cookie,
  local,
} from './utils/store';

// error show type
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// biz response structure
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

export const request: RequestConfig = {
  baseURL: API_HOST,

  // handle situation that parent of dataset contains whitespace that will be encoded to +, should be encoded to %20
  paramsSerializer(params) {
    return Object.keys(params)
      .filter((key) => params[key] !== undefined)
      .map(
        (key) =>
          `${key}=${
            typeof params[key] === 'string'
              ? params[key]
              : JSON.stringify(params[key])
          }`,
      )
      .join('&');
  },
  // errorThrower => errorHandler
  errorConfig: {
    errorThrower: (res: ResponseStructure) => {
      console.log('🚀 ~ errorThrower res:', res);
      // when explicitly axiosResponse.data.success = false
      // res here is pure biz response, not axiosResponse
      const { success, data, errorCode, errorMessage, showType } = res;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // throw biz error, will be handled in errorHandler
      }
    },
    // including both biz error and Axios Error
    errorHandler: (error: any, opts: any) => {
      console.log('🚀 ~ errorHandler error:', error);
      if (error.response) {
        trackError(ERROR_TYPES.GENERAL_SYSTEM_EXCEPTION);
        const { status } = error.response;
        if (status === 401 && ENABLE_HANDLE_401) {
          local.remove(LOCAL_TOKEN);
          history.push('/product-list');
          return;
        }
      }
      if (opts?.skipErrorHandler) throw error;
      if (error.name === 'BizError') {
        // biz error
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // axios error with response, not within 2xx
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // request send, but there is not response
        message.error('None response! Please retry.');
      } else {
        // error when sending request
        message.error('Request error, please retry.');
      }
    },
  },
  requestInterceptors: [
    (config: AxiosRequestConfig) => {
      const { headers, params, data, url } = config;

      const clonedHeaders = cloneDeep(headers ?? {});
      const token = local.get(LOCAL_TOKEN);
      const firstAuthToken = cookie.get(COOKIE_FIRST_AUTH_TOKEN);
      if (url?.includes('/v2/customer/user/auth')) {
        if (firstAuthToken) {
          clonedHeaders['x-axinan-authorization'] = firstAuthToken;
        }
      } else if (token) {
        clonedHeaders['x-axinan-authorization'] = token;
      }

      const newParams = decamelizeKeys(params ?? {});
      newParams.lang = getLocale();

      const newData =
        data instanceof FormData ? data : decamelizeKeys(data ?? {});

      return {
        ...config,
        headers: clonedHeaders,
        params: newParams,
        data: newData,
      };
    },
  ],
  responseInterceptors: [
    [
      // axios success
      (response: AxiosResponse) => {
        // response refer to https://raw.githubusercontent.com/matrixyf/pictureRepo/main/axiosReponse.png
        // will trigger errorConfig.errorThrower when response.data.success = false
        response.data = camelizeKeys(response.data);
        return response;
      },
      // axios error
      (error: RequestError) => {
        // error refer to https://raw.githubusercontent.com/matrixyf/pictureRepo/main/axiosError.png
        return Promise.reject(error);
      },
    ],
  ],
};

export function rootContainer(container: JSX.Element) {
  return <ErrorBoundary fallback={<Fallback />}>{container}</ErrorBoundary>;
}

export const getInitialState = async () => {
  const now = new Date().getTime();
  if (now > 1744164000000 && now < 1744167600000) {
    history.push('/maintenance');
  }
  return null;
};
